import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import blogsBg from '../assets/Mahi/25.jpeg';
import blogImage1 from '../assets/Mahi/7.jpeg';
import blogImage2 from '../assets/Mahi/21.jpeg';
import blogImage3 from '../assets/Mahi/32.jpeg';
import blogImage4 from '../assets/Mahi/3.jpeg';
import blogImage5 from '../assets/Mahi/105.jpeg';
import blogImage6 from '../assets/Mahi/20.jpeg';

// ────────────────────────────────────────────────
// Animation Variants
// ────────────────────────────────────────────────
const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: 'easeOut' } },
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.92 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } },
};

const modalAppear = {
    hidden: { opacity: 0, scale: 0.88, y: 30 },
    visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const Blogs = () => {
    const { t } = useTranslation('blogs');
    const [selectedPost, setSelectedPost] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const openModal = (post) => {
        setSelectedPost(post);
        setIsModalOpen(true);
        document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedPost(null);
        document.body.style.overflow = '';
    };

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isModalOpen) closeModal();
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isModalOpen]);

    const blogPosts = [
        { key: 'ayurveda', title: t('ayurvedaTitle'), excerpt: t('ayurvedaExcerpt'), description: t('ayurvedaDescription'), image: blogImage1, modalImages: [blogImage1], altTexts: [t('ayurvedaAlt')] },
        { key: 'healing', title: t('healingTitle'), excerpt: t('healingExcerpt'), description: t('healingDescription'), image: blogImage2, modalImages: [blogImage2], altTexts: [t('healingAlt')] },
        { key: 'nature', title: t('natureTitle'), excerpt: t('natureExcerpt'), description: t('natureDescription'), image: blogImage3, modalImages: [blogImage3], altTexts: [t('natureAlt')] },
        { key: 'food', title: t('foodTitle'), excerpt: t('foodExcerpt'), description: t('foodDescription'), image: blogImage4, modalImages: [blogImage4], altTexts: [t('foodAlt')] },
        { key: 'yoga', title: t('yogaTitle'), excerpt: t('yogaExcerpt'), description: t('yogaDescription'), image: blogImage5, modalImages: [blogImage5], altTexts: [t('yogaAlt')] },
        { key: 'lifestyle', title: t('lifestyleTitle'), excerpt: t('lifestyleExcerpt'), description: t('lifestyleDescription'), image: blogImage6, modalImages: [blogImage6], altTexts: [t('lifestyleAlt')] },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#f8f5ec] to-[#e8e4d6] font-sans">
            {/* ─── Hero ──────────────────────────────────────── */}
            <header className="relative h-[70vh] min-h-[480px] flex items-center justify-center overflow-hidden">
                <img
                    src={blogsBg}
                    alt={t('heroAlt')}
                    className="absolute inset-0 h-full w-full object-cover brightness-[0.65] scale-105 transition-transform duration-[20s]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/60" />

                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={fadeUp}
                    className="relative z-10 text-center px-5 sm:px-8 md:px-12 max-w-5xl"
                >
                    <h1 className="
            text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl 
            font-serif font-light tracking-tight text-white 
            drop-shadow-[0_4px_12px_rgba(0,0,0,0.7)]
          ">
                        {t('heroTitle')}
                    </h1>

                    <p className="
            mt-5 sm:mt-7 md:mt-9 
            text-lg sm:text-xl md:text-2xl lg:text-3xl 
            font-medium text-white/90 max-w-3xl mx-auto leading-relaxed
          ">
                        {t('heroSubtitle1')}{' '}
                        <span className="text-amber-400 font-medium drop-shadow">
                            {t('heroSubtitle2')}
                        </span>
                        {t('heroSubtitle3')}
                    </p>
                </motion.div>
            </header>

            {/* ─── Content ───────────────────────────────────── */}
            <main className="relative z-10 pb-16 md:pb-24 pt-12 md:pt-16">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
                    {/* Section Header */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        variants={fadeUp}
                        className="text-center mb-12 md:mb-16"
                    >
                        <div className="inline-flex items-center gap-4 mb-4">
                            <div className="h-px w-12 md:w-16 bg-teal-700/40 flex-1" />
                            <h2 className="
                text-3xl sm:text-4xl md:text-5xl font-serif font-light 
                text-[#1A2F2A] tracking-tight
              ">
                                {t('sectionTitle')}
                            </h2>
                            <div className="h-px w-12 md:w-16 bg-teal-700/40 flex-1" />
                        </div>

                        <p className="
              max-w-3xl mx-auto 
              text-base sm:text-lg md:text-xl 
              text-teal-800/80 leading-relaxed
            ">
                            {t('sectionDescription')}
                        </p>
                    </motion.div>

                    {/* Blog Grid */}
                    <div className="grid gap-6 sm:gap-7 md:gap-8 lg:gap-10 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                        {blogPosts.map((post, i) => (
                            <motion.article
                                key={post.key}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, margin: "-80px" }}
                                variants={scaleIn}
                                transition={{ delay: i * 0.08 }}
                                className="
                  group bg-white rounded-2xl overflow-hidden 
                  shadow-lg hover:shadow-2xl transition-all duration-500 
                  border border-teal-100/60 hover:border-teal-200
                  flex flex-col h-full
                "
                                onClick={() => openModal(post)}
                            >
                                <div className="relative aspect-[5/3] overflow-hidden">
                                    <img
                                        src={post.image}
                                        alt={post.title}
                                        className="
                      w-full h-full object-cover 
                      transition-transform duration-700 
                      group-hover:scale-110
                    "
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                </div>

                                <div className="p-6 md:p-7 flex flex-col flex-grow">
                                    <h3 className="
                    text-xl md:text-2xl font-light 
                    text-[#1A2F2A] group-hover:text-[#2D3F3A] 
                    transition-colors duration-300
                  ">
                                        {post.title}
                                    </h3>

                                    <p className="
                    mt-3 md:mt-4 
                    text-base text-gray-700 leading-relaxed 
                    line-clamp-3
                  ">
                                        {post.excerpt}
                                    </p>

                                    <div className="mt-auto pt-6">
                                        <span className="
                      inline-flex items-center gap-2 
                      text-[#E8C07D] font-medium 
                      group-hover:text-[#D4A860] transition-colors
                    ">
                                            {t('readMore')}
                                            <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </span>
                                    </div>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                </div>
            </main>

            {/* ─── Modal ─────────────────────────────────────── */}
            {isModalOpen && selectedPost && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
                    onClick={closeModal}
                >
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={modalAppear}
                        className="
              relative bg-gradient-to-b from-[#fdfaf2] to-[#f5f1e6] 
              rounded-2xl md:rounded-3xl shadow-2xl 
              w-full max-w-4xl max-h-[92vh] overflow-y-auto
              border border-teal-100/70
            "
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={closeModal}
                            className="
                absolute top-4 right-4 md:top-6 md:right-6 
                w-10 h-10 md:w-12 md:h-12 rounded-full 
                bg-white/80 hover:bg-white text-teal-900 
                flex items-center justify-center text-2xl md:text-3xl 
                shadow-md hover:shadow-lg transition-all z-10
              "
                            aria-label="Close"
                        >
                            ×
                        </button>

                        <div className="p-6 md:p-10 lg:p-12">
                            <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif font-light text-[#1A2F2A] mb-5 md:mb-7">
                                {selectedPost.title}
                            </h2>

                            <div className="prose prose-teal max-w-none text-gray-800 text-[15px] md:text-base lg:text-lg leading-relaxed">
                                {selectedPost.description}
                            </div>

                            {selectedPost.modalImages?.length > 0 && (
                                <div className="mt-8 md:mt-12 grid gap-5 sm:gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
                                    {selectedPost.modalImages.map((img, idx) => (
                                        <div
                                            key={idx}
                                            className="rounded-xl overflow-hidden shadow-md border border-teal-50"
                                        >
                                            <img
                                                src={img}
                                                alt={selectedPost.altTexts?.[idx] || selectedPost.title}
                                                className="w-full h-56 md:h-64 object-cover"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default Blogs;